import { z } from "zod";
import {
  Area,
  CalendarDate,
  EditorDocument,
  EntityId,
  Folder,
  Instant,
  Note,
  Priority,
  Project,
  ProjectStatus,
  Reminder,
  Tags,
  Task,
  TaskStatus,
} from "./entities";

const id = z.object({ id: EntityId });
const revisioned = id.extend({ baseRevision: z.number().int().nonnegative() });

/**
 * Every local domain mutation. Narrow field writes are applied last-writer-wins by
 * server arrival order. Only free-form content writes carry `baseRevision` and can conflict.
 */
export const Mutation = z.discriminatedUnion("type", [
  // tasks
  z.object({ type: z.literal("tasks.create"), task: Task }),
  id.extend({ type: z.literal("tasks.rename"), title: z.string() }),
  /** Plain Markdown. Drops any editor document: the Markdown is then the whole description. */
  revisioned.extend({
    type: z.literal("tasks.setDescription"),
    description: z.string().nullable(),
  }),
  /** The editor's document with its Markdown mirror, or `null` for both to clear it. */
  revisioned.extend({
    type: z.literal("tasks.setDescriptionContent"),
    document: EditorDocument.nullable(),
    markdown: z.string().nullable(),
  }),
  id.extend({ type: z.literal("tasks.complete"), completedAt: Instant }),
  id.extend({ type: z.literal("tasks.reopen") }),
  id.extend({ type: z.literal("tasks.setStatus"), status: TaskStatus }),
  id.extend({ type: z.literal("tasks.setDueDate"), dueDate: CalendarDate.nullable() }),
  id.extend({ type: z.literal("tasks.plan"), plannedDate: CalendarDate.nullable() }),
  id.extend({
    type: z.literal("tasks.schedule"),
    scheduledAt: Instant.nullable(),
    scheduledDurationMinutes: z.number().int().positive().nullable(),
  }),
  id.extend({ type: z.literal("tasks.moveToProject"), projectId: EntityId.nullable() }),
  id.extend({ type: z.literal("tasks.setPriority"), priority: Priority.nullable() }),
  id.extend({ type: z.literal("tasks.setEvening"), evening: z.boolean() }),
  id.extend({ type: z.literal("tasks.setHeading"), heading: z.string().nullable() }),
  id.extend({
    type: z.literal("tasks.setEstimate"),
    estimateMinutes: z.number().int().positive().nullable(),
  }),
  id.extend({ type: z.literal("tasks.setParent"), parentTaskId: EntityId.nullable() }),
  id.extend({ type: z.literal("tasks.setTags"), tags: Tags }),
  id.extend({ type: z.literal("tasks.reorder"), sortOrder: z.number() }),
  id.extend({ type: z.literal("tasks.delete") }),
  id.extend({ type: z.literal("tasks.restore") }),
  // notes
  z.object({ type: z.literal("notes.create"), note: Note }),
  /** Plain Markdown. Drops any editor document: the Markdown is then the whole note. */
  revisioned.extend({ type: z.literal("notes.updateMarkdown"), markdown: z.string() }),
  /** The editor's document with its Markdown mirror. */
  revisioned.extend({
    type: z.literal("notes.updateContent"),
    document: EditorDocument,
    markdown: z.string(),
  }),
  revisioned.extend({ type: z.literal("notes.rename"), title: z.string() }),
  id.extend({ type: z.literal("notes.move"), folderId: EntityId.nullable() }),
  id.extend({ type: z.literal("notes.reorder"), sortOrder: z.number() }),
  id.extend({ type: z.literal("notes.setPinned"), pinned: z.boolean() }),
  id.extend({ type: z.literal("notes.setTags"), tags: Tags }),
  id.extend({ type: z.literal("notes.delete") }),
  id.extend({ type: z.literal("notes.restore") }),
  // folders
  z.object({ type: z.literal("folders.create"), folder: Folder }),
  id.extend({ type: z.literal("folders.rename"), name: z.string() }),
  id.extend({ type: z.literal("folders.move"), parentId: EntityId.nullable() }),
  id.extend({ type: z.literal("folders.reorder"), sortOrder: z.number() }),
  id.extend({ type: z.literal("folders.delete") }),
  id.extend({ type: z.literal("folders.restore") }),
  // areas
  z.object({ type: z.literal("areas.create"), area: Area }),
  // projects
  z.object({ type: z.literal("projects.create"), project: Project }),
  id.extend({ type: z.literal("projects.rename"), name: z.string() }),
  id.extend({ type: z.literal("projects.setArea"), area: z.string().nullable() }),
  id.extend({ type: z.literal("projects.setStatus"), status: ProjectStatus }),
  id.extend({ type: z.literal("projects.setNotesFolder"), notesFolderId: EntityId.nullable() }),
  id.extend({ type: z.literal("projects.reorder"), sortOrder: z.number() }),
  id.extend({ type: z.literal("projects.delete") }),
  id.extend({ type: z.literal("projects.restore") }),
  // reminders
  z.object({ type: z.literal("reminders.create"), reminder: Reminder }),
  id.extend({ type: z.literal("reminders.reschedule"), triggerAt: Instant }),
  id.extend({ type: z.literal("reminders.snooze"), snoozedUntil: Instant }),
  id.extend({ type: z.literal("reminders.dismiss") }),
  id.extend({ type: z.literal("reminders.markDelivered") }),
  id.extend({ type: z.literal("reminders.delete") }),
]);
export type Mutation = z.infer<typeof Mutation>;
export type MutationType = Mutation["type"];
export type MutationOf<T extends MutationType> = Extract<Mutation, { type: T }>;

export const MutationEnvelope = z.object({
  /** Idempotency key. The server applies a given `mutationId` at most once. */
  mutationId: z.uuid(),
  deviceId: z.string().min(1),
  createdAt: Instant,
  mutation: Mutation,
});
export type MutationEnvelope = z.infer<typeof MutationEnvelope>;

/** The entity id a mutation targets, for dirty-tracking and conflict reporting. */
export function mutationTargetId(m: Mutation): string {
  switch (m.type) {
    case "tasks.create":
      return m.task.id;
    case "notes.create":
      return m.note.id;
    case "folders.create":
      return m.folder.id;
    case "areas.create":
      return m.area.id;
    case "projects.create":
      return m.project.id;
    case "reminders.create":
      return m.reminder.id;
    default:
      return m.id;
  }
}

export function mutationEntityKind(m: Mutation) {
  const ns = m.type.split(".")[0];
  switch (ns) {
    case "tasks":
      return "task" as const;
    case "notes":
      return "note" as const;
    case "folders":
      return "folder" as const;
    case "areas":
      return "area" as const;
    case "projects":
      return "project" as const;
    default:
      return "reminder" as const;
  }
}
